import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { RefreshCw, TrendingUp, Clock, CheckCircle2, XCircle } from 'lucide-react';
import { useMetrics } from '@/hooks/useJobs';
import StatsCard from '@/components/common/StatsCard';
import TimeRangeSelector from '@/components/common/TimeRangeSelector';
import type { TimeRange } from '@/lib/types';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';

const VALID_TIME_RANGES: TimeRange[] = ['15m', '1h', '24h', '7d', '30d', 'all'];

export default function MetricsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlTimeRange = searchParams.get('time_range');
  const initialTimeRange: TimeRange =
    urlTimeRange && VALID_TIME_RANGES.includes(urlTimeRange as TimeRange)
      ? (urlTimeRange as TimeRange)
      : '24h';

  const [timeRange, setTimeRange] = useState<TimeRange>(initialTimeRange);

  // Sync time range to URL
  useEffect(() => {
    setSearchParams(prev => {
      const params = new URLSearchParams(prev);
      if (timeRange === '24h') {
        params.delete('time_range');
      } else {
        params.set('time_range', timeRange);
      }
      return params;
    }, { replace: true });
  }, [timeRange, setSearchParams]);
  const { data: metrics, isLoading, refetch } = useMetrics(timeRange);

  if (isLoading) {
    return (
      <div className="p-8 flex items-center justify-center h-64">
        <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent"></div>
      </div>
    );
  }

  if (!metrics) {
    return (
      <div className="p-8">
        <h1 className="text-3xl font-bold mb-4">Metrics</h1>
        <p className="text-muted-foreground">No metrics data available</p>
      </div>
    );
  }

  // Prepare status distribution data for pie chart from job_activity
  const statusData = [
    { name: 'Completed', value: metrics.job_activity.recent_completed },
    { name: 'Failed', value: metrics.job_activity.recent_failed },
    { name: 'Pending', value: metrics.job_activity.recent_pending },
    { name: 'Running', value: metrics.job_activity.recent_running },
    { name: 'Retry Pending', value: metrics.job_activity.recent_retry_pending },
  ].filter(item => item.value > 0); // Only show statuses with jobs

  // Colors for status chart (matching badge colors)
  const statusColors: Record<string, string> = {
    Pending: '#f59e0b', // yellow
    Running: '#3b82f6', // blue
    Completed: '#10b981', // green
    Failed: '#ef4444', // red
    Cancelled: '#6b7280', // gray
    Expired: '#9ca3af', // light gray
    'Retry Pending': '#f97316', // orange
  };

  // Sorted copy for the breakdown table. Never sort statusData in place: the <Pie> below
  // references that same array and recharts pairs each <Cell> with its slice by index, so a
  // mutation here would rebind pie-slice colors to the wrong status.
  const sortedStatusData = [...statusData].sort((a, b) => b.value - a.value);
  const totalJobs = statusData.reduce((sum, s) => sum + s.value, 0);

  return (
    <div className="p-8 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Metrics</h1>
          <p className="text-sm text-muted-foreground mt-1">
            System-wide analytics and performance metrics
          </p>
        </div>
        <div className="flex items-center gap-3">
          <TimeRangeSelector value={timeRange} onChange={setTimeRange} />
          <button
            type="button"
            onClick={() => refetch()}
            className="flex items-center gap-2 px-4 py-2 rounded-md bg-primary/10 hover:bg-primary/20 text-primary border border-primary/30 transition-colors"
          >
            <RefreshCw className="h-4 w-4" />
            <span className="text-sm font-medium">Refresh</span>
          </button>
        </div>
      </div>

      {/* Key Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatsCard
          label="Completed"
          value={metrics.throughput.completed.toLocaleString()}
          icon={TrendingUp}
          variant="default"
        />
        <StatsCard
          label="Success Rate"
          value={`${metrics.job_activity.success_rate_percent.toFixed(1)}%`}
          icon={CheckCircle2}
          variant="success"
        />
        <StatsCard
          label="Jobs/Hour Avg"
          value={metrics.throughput.jobs_per_hour_avg.toFixed(1)}
          icon={Clock}
          variant="primary"
        />
        <StatsCard
          label="Failed Jobs"
          value={metrics.job_activity.recent_failed.toLocaleString()}
          icon={XCircle}
          variant="error"
        />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Status Distribution */}
        <div className="glass-card rounded-lg p-6">
          <h3 className="text-lg font-semibold mb-4">Status Distribution</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={statusData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {statusData.map((entry) => (
                  <Cell key={entry.name} fill={statusColors[entry.name] || '#6b7280'} />
                ))}
              </Pie>
              <Tooltip
                contentStyle={{
                  backgroundColor: 'rgba(2, 6, 23, 0.9)',
                  border: '1px solid rgba(59, 130, 246, 0.3)',
                  borderRadius: '8px',
                }}
              />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Status Breakdown Table */}
        <div className="glass-card rounded-lg p-6">
          <h3 className="text-lg font-semibold mb-4">Status Breakdown</h3>
          <div className="space-y-3">
            {sortedStatusData.map((item) => (
              <div key={item.name} className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: statusColors[item.name] || '#6b7280' }}
                  />
                  <span className="text-sm font-medium">{item.name}</span>
                </div>
                <div className="flex items-center gap-4">
                  <span className="text-sm text-muted-foreground">
                    {totalJobs > 0
                      ? ((item.value / totalJobs) * 100).toFixed(1)
                      : '0'}
                    %
                  </span>
                  <span className="text-sm font-mono">{item.value.toLocaleString()}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
